import { Component } from "@angular/core";
import { ConsumptionService } from "src/app/services/consumption.service";
import { GraphComponent } from "../graph/graph.component";
import { ChartData, ChartOptions, TooltipItem } from "chart.js";
import { DatePickerModule } from "primeng/datepicker";
import { ButtonModule } from "primeng/button";
import { FormsModule } from "@angular/forms";
import { SelectModule } from "primeng/select";
import { Ean } from "src/app/models/ean.model";
import { TabsModule } from "primeng/tabs";
import { be } from "src/app/app.config";
import { ContractService } from "src/app/services/contract.service";
import { NutsTypeEnum } from "src/app/models/enums/nutstype.enum";

@Component({
    selector: 'consumption-graph-component',
    standalone: true,
    templateUrl: './consumption-graph.component.html',
    styleUrls: ['./consumption-graph.component.scss'],
    imports: [ButtonModule, DatePickerModule, GraphComponent, FormsModule, SelectModule, TabsModule],
    providers: [ContractService, ConsumptionService],
})
export class ConsumptionGraphComponent {
    be = be;
    NutsType = NutsTypeEnum;
    selectedTab: string = 'Dag';
    eans: Ean[] = [];
    today: Date = new Date();
    consumptionDataPerDate: number[] = [];
    consumptionDataPerWeek: number[] = [];
    consumptionDataPerMonth: number[] = [];
    consumptionDataPerWeekDetails: number[] = [];
    consumptionDataPerYear: number[] = [];
    consumptionPeakPerWeek: number[] = [];
    consumptionPeakPerMonth: number[] = [];
    consumptionPeakPerYear: number[] = [];
    injectionDataPerDate: number[] = [];
    injectionDataPerWeek: number[] = [];
    injectionDataPerMonth: number[] = [];
    injectionDataPerWeekDetails: number[] = [];
    injectionDataPerYear: number[] = [];
    injectionPeakPerWeek: number[] = [];
    injectionPeakPerMonth: number[] = [];
    injectionPeakPerYear: number[] = [];
    selectedEAN!: Ean;
    selectedDate: Date = new Date();
    selectedWeek: Date[] = [new Date(new Date().setDate(new Date().getDate() - new Date().getDay())).addWeeks(-1).addDays(1), new Date(new Date().setDate(new Date().getDate() - new Date().getDay()))];
    selectedMonth: Date[] = [new Date(new Date().setDate(1)).addMonths(-new Date(new Date().setDate(1)).getMonth()), new Date(new Date().setDate(1))];
    selectedWeekDetails: Date[] = [new Date(new Date().setDate(new Date().getDate() - new Date().getDay())).addWeeks(-1).addDays(1), new Date(new Date().setDate(new Date().getDate() - new Date().getDay()))];
    selectedYear: Date[] = [new Date(new Date().setMonth(0)).addYears(-4), new Date(new Date().setMonth(0))];

    chartDataPerDate: ChartData = {
        xLabels: [ ],  
        datasets: [
            {
                type: "bar",
                label: "Verbruik per kwartier in KWh",
                data: {...this.consumptionDataPerDate},
                borderRadius: 2,
                stack: 'dag',
                backgroundColor: (ctx, options) => ctx?.parsed?.y >= 0 ? '#D1E09B' : '#BF8585',
                hoverBackgroundColor: (ctx, options) => ctx?.parsed?.y >= 0 ? '#A2C138' : '#991B1B',
            },
            {
                type: "bar",
                label: "Injectie per kwartier in KWh",
                data: {...this.injectionDataPerDate},
                borderRadius: 2,
                stack: 'dag',
                borderColor: '#87CEEB',
                backgroundColor: '#87CEEB',
            }
        ],
    };

    chartDataPerWeek: ChartData = {
        xLabels: [ ],  
        datasets: [
            {
                type: "bar",
                label: "Verbruik per dag in KWh",
                data: {...this.consumptionDataPerDate},
                borderRadius: 2,
                stack: 'week',
                backgroundColor: (ctx, options) => ctx?.parsed?.y >= 0 ? '#D1E09B' : '#BF8585',
                hoverBackgroundColor: (ctx, options) => ctx?.parsed?.y >= 0 ? '#A2C138' : '#991B1B',
            },
            {
                type: "line",
                label: "Maximaal verbruik in KWh",
                data: {...this.consumptionPeakPerWeek},
                borderColor: '#FFA500',
                backgroundColor: '#FFA500',
                fill: false,
                borderWidth: 1,
            },
            {
                type: "bar",
                label: "Injectie per dag in KWh",
                data: {...this.injectionDataPerDate},
                borderRadius: 2,
                stack: 'week',
                borderColor: '#87CEEB',
                backgroundColor: '#87CEEB',
            },
            {
                type: "line",
                label: "Maximaal injectie in KWh",
                data: {...this.injectionPeakPerWeek},
                borderColor: '#87CEEB',
                backgroundColor: '#87CEEB',
                fill: false,
                borderWidth: 1,
            }
        ],
    };

    chartDataPerMonth: ChartData = {
        xLabels: [  ],  
        datasets: [
            {
                type: "bar",
                label: "Verbruik per maand in KWh",
                data: {...this.consumptionDataPerDate},
                borderRadius: 2,
                stack: 'maand',
                backgroundColor: (ctx, options) => ctx?.parsed?.y >= 0 ? '#D1E09B' : '#BF8585',
                hoverBackgroundColor: (ctx, options) => ctx?.parsed?.y >= 0 ? '#A2C138' : '#991B1B',
            },
            {
                type: "line",
                label: "Maximaal verbruik in KWh",
                data: {...this.consumptionPeakPerMonth},
                borderColor: '#FFA500',
                backgroundColor: '#FFA500',
                fill: true,
                borderWidth: 1,
            },
            {
                type: "bar",
                label: "Injectie per maand in KWh",
                data: {...this.injectionDataPerDate},
                borderRadius: 2,
                stack: 'maand',
                borderColor: '#87CEEB',
                backgroundColor: '#87CEEB',
            },
            {
                type: "line",
                label: "Maximaal injectie in KWh",
                data: {...this.injectionPeakPerMonth},
                borderColor: '#87CEEB',
                backgroundColor: '#87CEEB',
                fill: true,
                borderWidth: 1,
            }
        ],
    };

    chartDataPerWeekDetails: ChartData = {
        xLabels: [ ],  
        datasets: [
            {
                type: "bar",
                label: "Verbruik per uur in KWh",
                data: {...this.consumptionDataPerDate},
                borderRadius: 2,
                stack: 'kwartaal',
                backgroundColor: (ctx, options) => ctx?.parsed?.y >= 0 ? '#D1E09B' : '#BF8585',
                hoverBackgroundColor: (ctx, options) => ctx?.parsed?.y >= 0 ? '#A2C138' : '#991B1B',
            },
            {
                type: "bar",
                label: "Injectie per uur in KWh",
                data: {...this.injectionDataPerDate},
                stack: 'kwartaal',
                borderColor: '#87CEEB',
                backgroundColor: '#87CEEB',
            }
        ],
    };

    chartDataPerYear: ChartData = {
        xLabels: [ ],  
        datasets: [
            {
                type: "bar",
                label: "Verbruik per jaar in KWh",
                data: {...this.consumptionDataPerDate},
                borderRadius: 2,
                stack: 'jaar',
                backgroundColor: (ctx, options) => ctx?.parsed?.y >= 0 ? '#D1E09B' : '#BF8585',
                hoverBackgroundColor: (ctx, options) => ctx?.parsed?.y >= 0 ? '#A2C138' : '#991B1B',
            },
            {
                type: "bar",
                label: "Injectie per jaar in KWh",
                data: {...this.injectionDataPerDate},
                stack: 'jaar',
                borderColor: '#87CEEB',
                backgroundColor: '#87CEEB',
            }
        ],
    };
    
    chartOptions: ChartOptions = {
        scales: {
            y: {
                beginAtZero: false,
                ticks: {
                    callback: (value) => `${value} KWh`
                }
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    callback: (value: string | number) => {
                        if (this.selectedTab === 'Dag') {
                            return this.chartDataPerDate?.xLabels?.at(parseInt(value.toString()))?.toString().replace(',', ':').replace(':0', ':00').replace('24', '00');
                        } else if (this.selectedTab === 'Week') {
                            const dayIndex = parseInt(value.toString());
                            const dayLabel = this.chartDataPerWeek?.xLabels?.at(dayIndex)?.toString();
                            if (dayLabel) {
                                if (this.selectedWeek[0] && this.selectedWeek[1]) {
                                    const days = Array.from({ length: (this.selectedWeek[1].getTime() - this.selectedWeek[0].getTime()) / (1000 * 60 * 60 * 24) + 1 }, (_, i) => {
                                        const date = new Date(this.selectedWeek[0]);
                                        date.setDate(date.getDate() + i);
                                        return date.toLocaleDateString('nl-BE', { weekday: 'long' });
                                    });
                                    return days[dayIndex];
                                } else if (this.selectedWeek[0]) {
                                    const days = Array.from({ length: 7 }, (_, i) => {
                                        const date = new Date(this.selectedWeek[0]);
                                        date.setDate(date.getDate() + i);
                                        return date.toLocaleDateString('nl-BE', { weekday: 'long' });
                                    });
                                    return days[dayIndex];
                                }
                            }
                            return value;
                        } else if (this.selectedTab === 'WeekDetails') {
                            var label = this.chartDataPerWeekDetails?.xLabels?.at(parseInt(value.toString()))?.toString();
                            if (this.selectedWeekDetails[0] && this.selectedWeekDetails[1]) {
                                var splits = label?.split(',') ?? [];
                                if (splits?.length > 2) {
                                    console.log(splits);
                                    var date = new Date(Date.parse(splits[2]));
                                    return date.toLocaleDateString('nl-BE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + splits[0].replace('24', '0') + ':' + splits[1].replace('0', '00');
                                }
                            }
                            return this.chartDataPerWeekDetails?.xLabels?.at(parseInt(value.toString()))?.toString().replace(',', ':').replace(':0', ':00').replace('24', '00');
                        } else if (this.selectedTab === 'Maand') {
                            return this.chartDataPerMonth?.xLabels?.at(parseInt(value.toString()))?.toString();
                        } else if (this.selectedTab === 'Jaar') {
                            return this.chartDataPerYear?.xLabels?.at(parseInt(value.toString()))?.toString();
                        }

                        return value;
                    }
                }
            }
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom'
            },
            tooltip: {
                callbacks: {
                    title: (tooltipItems: TooltipItem<'bar'>[]) => {
                        return tooltipItems.map((tooltipItem) => {
                            if (this.selectedTab === 'Dag') {
                                return tooltipItem.label.replace(',', ':').replace(':0', ':00').replace('24', '00');
                            } else if (this.selectedTab === 'Week') {
                                const dayLabel = this.chartDataPerWeek?.xLabels?.at(tooltipItem.dataIndex)?.toString();
                                if (dayLabel) {
                                    if (this.selectedWeek[0] && this.selectedWeek[1]) {
                                        const days = Array.from({ length: (this.selectedWeek[1].getTime() - this.selectedWeek[0].getTime()) / (1000 * 60 * 60 * 24) + 1 }, (_, i) => {
                                            const date = new Date(this.selectedWeek[0]);
                                            date.setDate(date.getDate() + i);
                                            return date.toLocaleDateString('nl-BE', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
                                        });
                                        return days[tooltipItem.dataIndex];
                                    } else if (this.selectedWeek[0]) {
                                        const days = Array.from({ length: 7 }, (_, i) => {
                                            const date = new Date(this.selectedWeek[0]);
                                            date.setDate(date.getDate() + i);
                                            return date.toLocaleDateString('nl-BE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
                                        });
                                        return days[tooltipItem.dataIndex];
                                    }
                                }
                                return tooltipItem.label;
                            } else if (this.selectedTab === 'WeekDetails') {
                                var label = tooltipItem.label;
                                if (this.selectedWeekDetails[0] && this.selectedWeekDetails[1]) {
                                    var splits = label.split(',');
                                    if (splits?.length > 2) {
                                        console.log(splits);
                                        var date = new Date(Date.parse(splits[2]));
                                        return date.toLocaleDateString('nl-BE', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' }) + ', ' + splits[0].replace('24', '0') + ':' + splits[1].replace('0', '00');
                                    }
                                }
                                return tooltipItem.label.replace(',', ':').replace(':0', ':00').replace('24', '00');
                            } else {
                                return tooltipItem.label;
                            }
                        });
                    },
                    label: (tooltipItem: TooltipItem<'bar'>) => {
                        return `${Math.round(tooltipItem.parsed.y)} KWh`;
                    }
                }
            }
        },
    };

    constructor(private contractService: ContractService, private consumptionService: ConsumptionService) {
        this.contractService.getContracts().subscribe(contracts => {
            this.eans = contracts.map(c => <Ean>{ type: c.installationType, ean: c.ean }).filter(e => e != null);
            this.selectedEAN = this.eans[0];
            this.generateDataForDate(this.selectedDate);
            this.generateDataForWeek(this.selectedWeek);
            this.generateDataForMonth(this.selectedMonth);
            this.generateDataForWeekDetails(this.selectedWeekDetails);
            this.generateDataForYear(this.selectedYear);
        });
    }

    generateDataForDate(date: Date): void {
        this.consumptionService.getQuarterlyConsumption(this.selectedEAN, date).subscribe(data => {
            this.consumptionDataPerDate = data.map(d => (d.quarterlyConsumption ?? 0));
            this.injectionDataPerDate = data.map(d => (d.quarterlyInjection ?? 0));
            this.chartDataPerDate.datasets[0].data = this.consumptionDataPerDate;
            this.chartDataPerDate.datasets[1].data = this.injectionDataPerDate;
            this.chartDataPerDate.xLabels = data.map(d => [d?.hour ?? 0, + (((d.quarter ?? 0) * 15) % 60)]);
            this.chartDataPerDate = {...this.chartDataPerDate};
        });
    }

    generateDataForWeek(dates: Date[]): void {
        if (dates.filter(x => x != null).length === 0) {
            return;
        }
        else if (dates.filter(x => x != null).length === 1) {
            this.consumptionService.getWeeklyConsumption(this.selectedEAN, dates[0]).subscribe(data => {
                this.consumptionDataPerWeek = data.map(d => (d.dayConsumption ?? 0) + (d.nightConsumption ?? 0));
                this.consumptionPeakPerWeek = data.map(d => d.maxPowerConsumption ?? 0);
                this.injectionDataPerWeek = data.map(d => (d.dayProduction ?? 0) + (d.nightProduction ?? 0));
                this.injectionPeakPerWeek = data.map(d => d.maxPowerProduction ?? 0);
                this.chartDataPerWeek.datasets[0].data = this.consumptionDataPerWeek;
                this.chartDataPerWeek.datasets[1].data = this.consumptionPeakPerWeek;
                this.chartDataPerWeek.datasets[2].data = this.injectionDataPerWeek;
                this.chartDataPerWeek.datasets[3].data = this.injectionPeakPerWeek;
                this.chartDataPerWeek.xLabels = data.map(d => d.day ?? 0);
                this.chartDataPerWeek = {...this.chartDataPerWeek};
            });
        }
        else {
            this.consumptionService.getWeeklyConsumptionBetweenDates(this.selectedEAN, dates[0], dates[1].addDays(1)).subscribe(data => {
                this.consumptionDataPerWeek = data.map(d => (d.dayConsumption ?? 0) + (d.nightConsumption ?? 0));
                this.consumptionPeakPerWeek = data.map(d => d.maxPowerConsumption ?? 0);
                this.injectionDataPerWeek = data.map(d => (d.dayProduction ?? 0) + (d.nightProduction ?? 0));
                this.injectionPeakPerWeek = data.map(d => d.maxPowerProduction ?? 0);
                this.chartDataPerWeek.datasets[0].data = this.consumptionDataPerWeek;
                this.chartDataPerWeek.datasets[1].data = this.consumptionPeakPerWeek;
                this.chartDataPerWeek.datasets[2].data = this.injectionDataPerWeek;
                this.chartDataPerWeek.datasets[3].data = this.injectionPeakPerWeek;
                this.chartDataPerWeek.xLabels = data.map(d => d.day ?? 0);
                this.chartDataPerWeek = {...this.chartDataPerWeek};
            });
        }

    }

    generateDataForMonth(dates: Date[]): void {
        if (dates.filter(x => x != null).length === 0) {
            return;
        }
        else if (dates.filter(x => x != null).length === 1) {
            this.consumptionService.getMonthlyConsumption(this.selectedEAN, dates[0]).subscribe(data => {
                this.consumptionDataPerMonth = data.map(d => (d.dayConsumption ?? 0) + (d.nightConsumption ?? 0));
                this.consumptionPeakPerMonth = data.map(d => d.maxPowerConsumption ?? 0);
                this.injectionDataPerMonth = data.map(d => (d.dayProduction ?? 0) + (d.nightProduction ?? 0));
                this.injectionPeakPerMonth = data.map(d => d.maxPowerProduction ?? 0);
                this.chartDataPerMonth.datasets[0].data = this.consumptionDataPerMonth;
                this.chartDataPerMonth.datasets[1].data = this.consumptionPeakPerMonth;
                this.chartDataPerMonth.datasets[2].data = this.injectionDataPerMonth;
                this.chartDataPerMonth.datasets[3].data = this.injectionPeakPerMonth;
                this.chartDataPerMonth.xLabels = data.map(d => { if (be.monthNames) { return (be.monthNames[(d.month ?? 0) - 1] ?? '') } else { return '' } });
                this.chartDataPerMonth = {...this.chartDataPerMonth};
            });
        }
        else {
            this.consumptionService.getMonthlyConsumptionBetweenDates(this.selectedEAN, dates[0], dates[1].addMonths(1)).subscribe(data => {
                this.consumptionDataPerMonth = data.map(d => (d.dayConsumption ?? 0) + (d.nightConsumption ?? 0));
                this.consumptionPeakPerMonth = data.map(d => d.maxPowerConsumption ?? 0);
                this.injectionDataPerMonth = data.map(d => (d.dayProduction ?? 0) + (d.nightProduction ?? 0));
                this.injectionPeakPerMonth = data.map(d => d.maxPowerProduction ?? 0);
                this.chartDataPerMonth.datasets[0].data = this.consumptionDataPerMonth;
                this.chartDataPerMonth.datasets[1].data = this.consumptionPeakPerMonth;
                this.chartDataPerMonth.datasets[2].data = this.injectionDataPerMonth;
                this.chartDataPerMonth.datasets[3].data = this.injectionPeakPerMonth;
                this.chartDataPerMonth.xLabels = data.map(d => { if (be.monthNames) { return (be.monthNames[(d.month ?? 0) - 1] ?? '') } else { return '' } });
                this.chartDataPerMonth = {...this.chartDataPerMonth};
            });
        }
    }

    generateDataForWeekDetails(dates: Date[]): void {
        if (dates.filter(x => x != null).length === 0) {
            return;
        }
        else if (dates.filter(x => x != null).length === 1) {
            this.consumptionService.getQuarterlyConsumption(this.selectedEAN, dates[0]).subscribe(data => {
                this.consumptionDataPerWeekDetails = data.map(d => (d.quarterlyConsumption ?? 0));
                this.injectionDataPerWeekDetails = data.map(d => (d.quarterlyInjection ?? 0));
                this.chartDataPerWeekDetails.datasets[0].data = this.consumptionDataPerWeekDetails;
                this.chartDataPerWeekDetails.datasets[1].data = this.injectionDataPerWeekDetails;
                this.chartDataPerWeekDetails.xLabels = data.map(d => [d?.hour ?? 0, + (((d.quarter ?? 0) * 15) % 60)]);
                this.chartDataPerWeekDetails = {...this.chartDataPerWeekDetails};
            });
        }
        else {
            this.consumptionService.getQuarterlyConsumptionBetweenDates(this.selectedEAN, dates[0], dates[1]).subscribe(data => {
                this.consumptionDataPerWeekDetails = data.map(d => (d.quarterlyConsumption ?? 0));
                this.injectionDataPerWeekDetails = data.map(d => (d.quarterlyInjection ?? 0));
                this.chartDataPerWeekDetails.datasets[0].data = this.consumptionDataPerWeekDetails;
                this.chartDataPerWeekDetails.datasets[1].data = this.injectionDataPerWeekDetails;
                this.chartDataPerWeekDetails.xLabels = data.map(d => [d?.hour ?? 0, + (((d.quarter ?? 0) * 15) % 60), d?.date]);
                this.chartDataPerWeekDetails = {...this.chartDataPerWeekDetails};
            });
        }
    }

    generateDataForYear(dates: Date[]): void {
        if (dates.length === 0) {
            return;
        }
        else if (dates.filter(x => x != null).length === 1) {
            this.consumptionService.getYearlyConsumption(this.selectedEAN, dates[0]).subscribe(data => {
                this.consumptionDataPerYear = data.map(d => (d.yearlyConsumption ?? 0));
                this.injectionDataPerYear = data.map(d => (d.yearlyInjection ?? 0));
                this.chartDataPerYear.datasets[0].data = this.consumptionDataPerYear;
                this.chartDataPerYear.datasets[1].data = this.injectionDataPerYear;
                this.chartDataPerYear.xLabels = data.map(d => d.year ?? 0);
                this.chartDataPerYear = {...this.chartDataPerYear};
            });
        }
        else {
            this.consumptionService.getYearlyConsumptionBetweenDates(this.selectedEAN, dates[0], dates[1].addYears(1)).subscribe(data => {
                this.consumptionDataPerYear = data.map(d => (d.yearlyConsumption ?? 0));
                this.injectionDataPerYear = data.map(d => (d.yearlyInjection ?? 0));
                this.chartDataPerYear.datasets[0].data = this.consumptionDataPerYear;
                this.chartDataPerYear.datasets[1].data = this.injectionDataPerYear;
                this.chartDataPerYear.xLabels = data.map(d => d.year ?? 0);
                this.chartDataPerYear = {...this.chartDataPerYear};
            });
        }
    }

    clockSymbol(hour: number, minute: number = 0): string {
        if (minute === 0) {
            switch (hour) {
                case 0:
                    return '🕛';
                case 1:
                    return '🕐';
                case 2:
                    return '🕑';
                case 3:
                    return '🕒';
                case 4:
                    return '🕓';
                case 5:
                    return '🕔';
                case 6:
                    return '🕕';
                case 7:
                    return '🕖';
                case 8:
                    return '🕗';
                case 9:
                    return '🕘';
                case 10:
                    return '🕙';
                case 11:
                    return '🕚';
                case 12:
                    return '🕛';
                case 13:
                    return '🕐';
                case 14:
                    return '🕑';
                case 15:
                    return '🕒';
                case 16:
                    return '🕓';
                case 17:
                    return '🕔';
                case 18:
                    return '🕕';
                case 19:
                    return '🕖';
                case 20:
                    return '🕗';
                case 21:
                    return '🕘';
                case 22:
                    return '🕙';
                case 23:
                    return '🕚';
                default:
                    return '🕛';
            }
        } else if (minute === 15) {
            switch (hour) {
                case 0:
                    return '🕧';
                case 1:
                    return '🕜';
                case 2:
                    return '🕝';
                case 3:
                    return '🕞';
                case 4:
                    return '🕟';
                case 5:
                    return '🕠';
                case 6:
                    return '🕡';
                case 7:
                    return '🕢';
                case 8:
                    return '🕣';
                case 9:
                    return '🕤';
                case 10:
                    return '🕥';
                case 11:
                    return '🕦';
                case 12:
                    return '🕧';
                case 13:
                    return '🕜';
                case 14:
                    return '🕝';
                case 15:
                    return '🕞';
                case 16:
                    return '🕟';
                case 17:
                    return '🕠';
                case 18:
                    return '🕡';
                case 19:
                    return '🕢';
                case 20:
                    return '🕣';
                case 21:
                    return '🕤';
                case 22:
                    return '🕥';
                case 23:
                    return '🕦';
                default:
                    return '🕧';
            }
        } else if (minute === 30) {
            switch (hour) {
                case 0:
                    return '🕧';
                case 1:
                    return '🕜';
                case 2:
                    return '🕝';
                case 3:
                    return '🕞';
                case 4:
                    return '🕟';
                case 5:
                    return '🕠';
                case 6:
                    return '🕡';
                case 7:
                    return '🕢';
                case 8:
                    return '🕣';
                case 9:
                    return '🕤';
                case 10:
                    return '🕥';
                case 11:
                    return '🕦';
                case 12:
                    return '🕧';
                case 13:
                    return '🕜';
                case 14:
                    return '🕝';
                case 15:
                    return '🕞';
                case 16:
                    return '🕟';
                case 17:
                    return '🕠';
                case 18:
                    return '🕡';
                case 19:
                    return '🕢';
                case 20:
                    return '🕣';
                case 21:
                    return '🕤';
                case 22:
                    return '🕥';
                case 23:
                    return '🕦';
                default:
                    return '🕧';
            }
        } else if (minute === 45) {
            switch (hour) {
                case 0:
                    return '🕛';
                case 1:
                    return '🕐';
                case 2:
                    return '🕑';
                case 3:
                    return '🕒';
                case 4:
                    return '🕓';
                case 5:
                    return '🕔';
                case 6:
                    return '🕕';
                case 7:
                    return '🕖';
                case 8:
                    return '🕗';
                case 9:
                    return '🕘';
                case 10:
                    return '🕙';
                case 11:
                    return '🕚';
                case 12:
                    return '🕛';
                case 13:
                    return '🕐';
                case 14:
                    return '🕑';
                case 15:
                    return '🕒';
                case 16:
                    return '🕓';
                case 17:
                    return '🕔';
                case 18:
                    return '🕕';
                case 19:
                    return '🕖';
                case 20:
                    return '🕗';
                case 21:
                    return '🕘';
                case 22:
                    return '🕙';
                case 23:
                    return '🕚';
                default:
                    return '🕛';
            }
        }

        return '🕛';
    }
}